/**
 * CSV of resolved rows. A raw event name is its own column when a label is shown.
 */
import { cellRaw, cellText, rawColumnSlots } from './shape.js';

/**
 * @param {string} value
 * @returns {string}
 */
function csvEscape(value) {
  const text = value == null ? '' : String(value);
  if (/[",\n\r]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

/**
 * @param {string[][]} matrix
 * @returns {string}
 */
export function toCsv(matrix) {
  return matrix.map((row) => row.map(csvEscape).join(',')).join('\n');
}

/**
 * @param {Array<{ key: string, label?: string }>} columns
 * @param {Array<Record<string, unknown>>} rows
 * @returns {string[][]}
 */
export function csvMatrix(columns, rows) {
  const slots = rawColumnSlots(columns, rows);
  const headers = [];
  columns.forEach((column, index) => {
    const label = column.label || column.key;
    headers.push(label);
    if (slots[index]) headers.push(`${label} raw`);
  });
  const body = rows.map((row) => {
    const line = [];
    columns.forEach((column, index) => {
      line.push(cellText(row[column.key]));
      if (slots[index]) line.push(cellRaw(row[column.key]));
    });
    return line;
  });
  return [headers, ...body];
}

/**
 * @param {string} title
 * @returns {string}
 */
export function csvFilename(title) {
  const slug = String(title || 'block').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  return `${slug || 'block'}.csv`;
}

/**
 * @param {string} filename
 * @param {string[][]} matrix
 * @returns {string}
 */
export function downloadCsv(filename, matrix) {
  const csv = toCsv(matrix);
  if (typeof document === 'undefined' || !document.body || typeof URL.createObjectURL !== 'function') return csv;
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  document.body.append(link);
  if (typeof link.click === 'function') link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return csv;
}
